/*
  Nische collection page.

  Everything the page needs in one deferred file: filtering, sorting, the
  filter drawer, the desktop dropdowns, the price slider, the density toggle,
  recently viewed and back-to-top.

  Design notes worth knowing before editing:

  - The filter markup is rendered ONCE and moved between the drawer, the
    sidebar and the desktop dropdowns by `matchMedia`. Rendering it twice
    would mean two sources of truth for one checkbox, which is how filter UIs
    normally start disagreeing with the URL. The element is moved, never its
    children, so every `#nis-...` rule keeps matching it.

  - The URL is the single source of truth. Every apply writes it, popstate
    reads it back, and the fetch is derived from it - so the back button, a
    shared link and a reload all land on the same result.

  - Filtering swaps `#ProductGridContainer`'s innerHTML, exactly like Dawn's
    own facets do, because `nische-infinite-scroll.js` already watches that
    element and re-binds itself off a childList mutation. Keeping that
    contract is why the two features do not need to know about each other.

  - Facet options are updated in place rather than re-rendered: the groups may
    have been moved into dropdown panels by then, and a wholesale replace
    would also throw away which accordions the shopper had opened.
*/
(function () {
  'use strict';

  if (window.NischeCollection) return;

  var DENSITY_KEY = 'nische:density';
  var RECENT_KEY = 'nische:recent';
  var RECENT_MAX = 20;
  var DESKTOP = '(min-width: 990px)';

  /* --------------------------------------------------------------- helpers */

  function q(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /*
    localStorage throws outright in some privacy modes rather than returning
    null, so every touch of it is wrapped. A shopper with it blocked loses the
    remembered density and the recently-viewed rail, and nothing else.
  */
  function readStore(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* full, or blocked - not worth reporting to the shopper */
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function money(n) {
    // The page renders one currency; the symbol comes from the markup, not here.
    return String(Math.round(n));
  }

  /* ------------------------------------------------------------- the page */

  function Collection(root) {
    this.root = root;
    this.sectionId = root.getAttribute('data-nis-section');
    this.basePath = root.getAttribute('data-nis-base') || window.location.pathname;
    this.facets = q('[data-nis-facets]', root);
    this.results = q('[data-nis-results]', root);
    this.chips = q('[data-nis-chips]', root);
    this.drawer = q('[data-nisf-sheet]', root);
    this.drawerBody = q('[data-nisf-sheet-body]', root);
    this.side = q('[data-nis-side]', root);
    this.layout = root.getAttribute('data-nis-layout') || 'drawer';
    this.cache = new Map();
    this.controller = null;
    this.lastFocus = null;

    this.apply = debounce(this.applyNow.bind(this), 180);

    this.placeFacets();
    this.bind();
    this.syncChips();
    this.restoreDensity();
    this.mountRecent();
  }

  /* -------------------------------------------------- moving the filter UI */

  Collection.prototype.placeFacets = function () {
    if (!this.facets) return;
    var self = this;
    var mq = window.matchMedia(DESKTOP);

    var move = function () {
      if (mq.matches && self.layout === 'sidebar' && self.side) {
        if (self.facets.parentNode !== self.side) self.side.appendChild(self.facets);
      } else if (mq.matches && self.layout === 'horizontal') {
        // Each group goes to the dropdown panel that names it.
        qa('[data-nis-group]', self.root).forEach(function (group) {
          var slot = q('[data-nis-drop-for="' + group.getAttribute('data-nis-group') + '"]', self.root);
          if (slot && group.parentNode !== slot) {
            group.open = true;
            slot.appendChild(group);
          }
        });
      } else {
        // Phone, tablet, or the drawer layout: everything lives in the sheet.
        if (self.layout === 'horizontal') {
          var list = q('[data-nis-facet-list]', self.facets) || self.facets;
          qa('[data-nis-group]', self.root)
            .sort(function (a, b) {
              return Number(a.getAttribute('data-nis-group')) - Number(b.getAttribute('data-nis-group'));
            })
            .forEach(function (group) {
              if (group.parentNode !== list) list.appendChild(group);
            });
        }
        if (self.drawerBody && self.facets.parentNode !== self.drawerBody) {
          self.drawerBody.appendChild(self.facets);
        }
      }
    };

    move();
    // `change` fires on the breakpoint crossing only, so this costs nothing while scrolling.
    if (mq.addEventListener) mq.addEventListener('change', move);
    else if (mq.addListener) mq.addListener(move);
  };

  /* ---------------------------------------------------------------- events */

  Collection.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-nis-filter]')) {
        self.apply();
      } else if (t.matches('[data-nis-sort]')) {
        self.applyNow();
      } else if (t.matches('[data-nis-price-input]')) {
        self.syncSliderFromInputs();
        self.apply();
      }
    });

    this.root.addEventListener('input', function (e) {
      if (e.target.matches('[data-nis-price-range]')) {
        self.syncInputsFromSlider(e.target);
        self.apply();
      }
      if (e.target.matches('[data-nis-facet-search]')) {
        self.filterOptions(e.target);
      }
    });

    this.root.addEventListener('click', function (e) {
      var open = e.target.closest('[data-nis-open]');
      if (open) {
        e.preventDefault();
        self.openDrawer(open);
        return;
      }
      if (e.target.closest('[data-nis-close]')) {
        e.preventDefault();
        self.closeDrawer();
        return;
      }
      var chip = e.target.closest('[data-nis-chip]');
      if (chip) {
        e.preventDefault();
        self.removeFilter(chip.getAttribute('data-nis-name'), chip.getAttribute('data-nis-value'));
        return;
      }
      if (e.target.closest('[data-nis-clear]')) {
        e.preventDefault();
        self.clearAll();
        return;
      }
      var dens = e.target.closest('[data-nis-density]');
      if (dens) {
        e.preventDefault();
        self.setDensity(dens.getAttribute('data-nis-density'), true);
        return;
      }
      var drop = e.target.closest('[data-nis-drop-btn]');
      if (drop) {
        e.preventDefault();
        self.toggleDrop(drop);
        return;
      }
      if (!e.target.closest('[data-nis-drop]')) self.closeDrops();
      if (e.target.closest('[data-nis-top]')) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.drawer && self.drawer.classList.contains('is-open')) self.closeDrawer();
      self.closeDrops();
    });

    window.addEventListener('popstate', function () {
      self.render(window.location.href, false);
      self.syncFromUrl();
    });

    this.mountBackToTop();
  };

  /* --------------------------------------------------------------- applying */

  Collection.prototype.currentParams = function () {
    var params = new URLSearchParams();

    qa('[data-nis-filter]', this.root).forEach(function (input) {
      if (input.type === 'checkbox' && input.checked) params.append(input.name, input.value);
    });

    qa('[data-nis-price-input]', this.root).forEach(function (input) {
      var value = input.value.trim();
      if (value === '') return;
      var bound = Number(input.getAttribute('data-nis-bound'));
      // Only send a bound the shopper actually moved - a min equal to the
      // collection's own minimum is not a filter, it is the default.
      if (Number(value) === bound) return;
      params.set(input.name, value);
    });

    // Only carry `sort_by` when the shopper actually chose something other
    // than the collection's own order - otherwise every filtered URL grows a
    // parameter that changes nothing.
    var sort = q('[data-nis-sort]', this.root);
    if (sort && sort.value && sort.value !== sort.getAttribute('data-nis-default')) {
      params.set('sort_by', sort.value);
    }

    return params;
  };

  Collection.prototype.applyNow = function () {
    var params = this.currentParams();
    var qs = params.toString();
    // The search page's base already carries `?q=` - join with `&` there.
    var join = this.basePath.indexOf('?') === -1 ? '?' : '&';
    var url = this.basePath + (qs ? join + qs : '');
    window.history.pushState({ nis: true }, '', url);
    this.render(url, true);
    this.syncChips();
  };

  Collection.prototype.removeFilter = function (name, value) {
    qa('[data-nis-filter]', this.root).forEach(function (input) {
      if (input.name === name && input.value === value) input.checked = false;
    });
    if (name.indexOf('.price.') !== -1) this.resetPrice();
    this.applyNow();
  };

  Collection.prototype.clearAll = function () {
    qa('[data-nis-filter]', this.root).forEach(function (input) {
      input.checked = false;
    });
    this.resetPrice();
    this.applyNow();
  };

  Collection.prototype.resetPrice = function () {
    qa('[data-nis-price-input]', this.root).forEach(function (input) {
      input.value = input.getAttribute('data-nis-bound');
    });
    qa('[data-nis-price-range]', this.root).forEach(function (range) {
      range.value = range.getAttribute('data-nis-bound');
    });
    this.paintPriceFill();
  };

  /* ----------------------------------------------------------- the request */

  Collection.prototype.render = function (url, push) {
    var self = this;
    var fetchUrl = new URL(url, window.location.origin);
    fetchUrl.searchParams.set('section_id', this.sectionId);
    var key = fetchUrl.toString();

    if (this.results) this.results.setAttribute('data-busy', 'true');

    var done = function (html) {
      self.paint(html);
      if (self.results) self.results.removeAttribute('data-busy');
    };

    if (this.cache.has(key)) {
      done(this.cache.get(key));
      return;
    }

    // A shopper ticking three boxes quickly should pay for one render, not three.
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    fetch(key, { signal: this.controller.signal })
      .then(function (res) {
        // The Section Rendering API answers 404 with an EMPTY BODY for a bad
        // section id, which reads as a successful empty page unless the status
        // is checked. Do not remove this.
        if (!res.ok) throw new Error('Section render failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        self.cache.set(key, html);
        done(html);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        if (self.results) self.results.removeAttribute('data-busy');
        // Leave the shopper on a working page and let the URL do the work.
        window.location.href = url;
      });
  };

  Collection.prototype.paint = function (html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var incomingGrid = doc.getElementById('ProductGridContainer');
    var grid = document.getElementById('ProductGridContainer');
    if (incomingGrid && grid) grid.innerHTML = incomingGrid.innerHTML;

    var incomingCount = q('[data-nis-count]', doc);
    qa('[data-nis-count]', this.root).forEach(function (el) {
      if (incomingCount) el.textContent = incomingCount.textContent;
    });

    var incomingShow = q('[data-nis-showcount]', doc);
    var showEl = q('[data-nis-showcount]', this.root);
    if (incomingShow && showEl) showEl.textContent = incomingShow.textContent;

    this.updateCounts(doc);
    this.applyDensityClasses();

    // Hearts are localStorage state, so the server cannot render them filled.
    if (window.NischeWishlist && window.NischeWishlist.hydrate) {
      window.NischeWishlist.hydrate(grid || document);
    }

    // Only pull the page up when the results have scrolled off the top -
        // filtering from halfway down a long grid should not throw the shopper
    // back to the banner.
    if (this.results) {
      var top = this.results.getBoundingClientRect().top;
      if (top < 0) {
        window.scrollTo({ top: window.scrollY + top - 80, behavior: 'smooth' });
      }
    }

    this.root.dispatchEvent(new CustomEvent('nische:collection:rendered', { bubbles: true }));
  };

  /*
    Counts are copied option by option rather than by replacing the group: by
    now the groups may live inside dropdown panels, and a replace would also
    discard which accordions were open.
  */
  Collection.prototype.updateCounts = function (doc) {
    var incoming = {};
    qa('[data-nis-filter]', doc).forEach(function (input) {
      incoming[input.name + '||' + input.value] = input;
    });

    qa('[data-nis-filter]', this.root).forEach(function (input) {
      var match = incoming[input.name + '||' + input.value];
      var row = input.closest('.nis-opt');
      if (!row) return;
      var countEl = q('.nis-opt__count', row);
      if (!match) {
        row.classList.add('nis-opt--empty');
        return;
      }
      var count = match.getAttribute('data-nis-optcount');
      if (countEl && count !== null) countEl.textContent = count;
      var empty = count === '0';
      row.classList.toggle('nis-opt--empty', empty);
      input.disabled = empty && !input.checked;
    });

    this.syncGroupCounts();
  };

  Collection.prototype.syncGroupCounts = function () {
    qa('[data-nis-group]', this.root).forEach(function (group) {
      var n = qa('[data-nis-filter]:checked', group).length;
      var badge = q('[data-nis-group-sel]', group);
      if (badge) badge.textContent = n ? String(n) : '';
      var id = group.getAttribute('data-nis-group');
      var dropCount = q('[data-nis-drop-count="' + id + '"]');
      if (dropCount) {
        dropCount.textContent = String(n);
        dropCount.hidden = n === 0;
      }
    });
  };

  /* ------------------------------------------------------------- the chips */

  Collection.prototype.syncChips = function () {
    if (!this.chips) return;
    var self = this;
    var frag = document.createDocumentFragment();
    var total = 0;

    qa('[data-nis-filter]:checked', this.root).forEach(function (input) {
      var row = input.closest('.nis-opt');
      var label = row ? (q('.nis-opt__label', row) || {}).textContent : input.value;
      frag.appendChild(self.chip((label || input.value).trim(), input.name, input.value));
      total += 1;
    });

    var priceLabel = this.priceChipLabel();
    if (priceLabel) {
      frag.appendChild(self.chip(priceLabel, 'price', ''));
      total += 1;
    }

    this.chips.innerHTML = '';
    if (total) {
      this.chips.appendChild(frag);
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'nis-chips__clear';
      clear.setAttribute('data-nis-clear', '');
      clear.textContent = this.chips.getAttribute('data-nis-clear-label') || 'Clear all';
      this.chips.appendChild(clear);
    }
    this.chips.hidden = total === 0;

    qa('[data-nis-badge]', this.root).forEach(function (badge) {
      badge.textContent = String(total);
      badge.hidden = total === 0;
    });

    this.syncGroupCounts();
  };

  Collection.prototype.chip = function (label, name, value) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'nis-chip';
    btn.setAttribute('data-nis-chip', '');
    btn.setAttribute('data-nis-name', name);
    btn.setAttribute('data-nis-value', value);
    btn.setAttribute('aria-label', 'Remove filter: ' + label);
    var text = document.createElement('span');
    text.textContent = label;
    btn.appendChild(text);
    btn.insertAdjacentHTML(
      'beforeend',
      '<svg viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>'
    );
    return btn;
  };

  Collection.prototype.priceChipLabel = function () {
    var min = q('[data-nis-price-input][data-nis-edge="min"]', this.root);
    var max = q('[data-nis-price-input][data-nis-edge="max"]', this.root);
    if (!min || !max) return '';
    var changed =
      Number(min.value) !== Number(min.getAttribute('data-nis-bound')) ||
      Number(max.value) !== Number(max.getAttribute('data-nis-bound'));
    if (!changed) return '';
    var sym = this.root.getAttribute('data-nis-currency') || '';
    return sym + money(min.value) + ' - ' + sym + money(max.value);
  };

  /*
    Called after popstate, when the inputs and the URL have parted company.
  */
  Collection.prototype.syncFromUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var wanted = {};
    params.forEach(function (value, name) {
      wanted[name + '||' + value] = true;
    });

    qa('[data-nis-filter]', this.root).forEach(function (input) {
      input.checked = !!wanted[input.name + '||' + input.value];
    });

    qa('[data-nis-price-input]', this.root).forEach(function (input) {
      input.value = params.get(input.name) || input.getAttribute('data-nis-bound');
    });
    qa('[data-nis-price-range]', this.root).forEach(function (range) {
      range.value = params.get(range.getAttribute('data-nis-for')) || range.getAttribute('data-nis-bound');
    });
    this.paintPriceFill();

    var sort = q('[data-nis-sort]', this.root);
    if (sort) sort.value = params.get('sort_by') || sort.getAttribute('data-nis-default') || sort.value;

    this.syncChips();
  };

  /* ------------------------------------------------------------ price range */

  Collection.prototype.priceParts = function () {
    return {
      minInput: q('[data-nis-price-input][data-nis-edge="min"]', this.root),
      maxInput: q('[data-nis-price-input][data-nis-edge="max"]', this.root),
      minRange: q('[data-nis-price-range][data-nis-edge="min"]', this.root),
      maxRange: q('[data-nis-price-range][data-nis-edge="max"]', this.root),
      fill: q('[data-nis-price-fill]', this.root)
    };
  };

  Collection.prototype.syncInputsFromSlider = function (moved) {
    var p = this.priceParts();
    if (!p.minRange || !p.maxRange) return;
    var min = Number(p.minRange.value);
    var max = Number(p.maxRange.value);
    // The two thumbs share a track and must not pass each other.
    if (min > max) {
      if (moved === p.minRange) p.maxRange.value = String(min);
      else p.minRange.value = String(max);
      min = Number(p.minRange.value);
      max = Number(p.maxRange.value);
    }
    if (p.minInput) p.minInput.value = String(min);
    if (p.maxInput) p.maxInput.value = String(max);
    this.paintPriceFill();
  };

  Collection.prototype.syncSliderFromInputs = function () {
    var p = this.priceParts();
    if (!p.minRange || !p.maxRange || !p.minInput || !p.maxInput) return;
    var floor = Number(p.minRange.min);
    var ceil = Number(p.maxRange.max);
    var min = Math.max(floor, Math.min(ceil, Number(p.minInput.value || floor)));
    var max = Math.max(floor, Math.min(ceil, Number(p.maxInput.value || ceil)));
    if (min > max) min = max;
    p.minInput.value = String(min);
    p.maxInput.value = String(max);
    p.minRange.value = String(min);
    p.maxRange.value = String(max);
    this.paintPriceFill();
  };

  Collection.prototype.paintPriceFill = function () {
    var p = this.priceParts();
    if (!p.fill || !p.minRange || !p.maxRange) return;
    var floor = Number(p.minRange.min);
    var ceil = Number(p.maxRange.max);
    var span = ceil - floor || 1;
    var left = ((Number(p.minRange.value) - floor) / span) * 100;
    var right = 100 - ((Number(p.maxRange.value) - floor) / span) * 100;
    p.fill.style.left = left + '%';
    p.fill.style.right = right + '%';
  };

  /* -------------------------------------------------------- search a facet */

  Collection.prototype.filterOptions = function (input) {
    var group = input.closest('[data-nis-group]');
    if (!group) return;
    var term = input.value.trim().toLowerCase();
    var shown = 0;
    qa('.nis-opt', group).forEach(function (row) {
      var label = (q('.nis-opt__label', row) || {}).textContent || '';
      var hit = term === '' || label.toLowerCase().indexOf(term) !== -1;
      row.hidden = !hit;
      if (hit) shown += 1;
    });
    var none = q('[data-nis-facet-none]', group);
    if (none) none.hidden = shown !== 0;
  };

  /* ------------------------------------------------------------- the drawer */

  Collection.prototype.openDrawer = function (trigger) {
    if (!this.drawer) return;
    this.lastFocus = trigger || document.activeElement;
    this.drawer.classList.add('is-open');
    this.drawer.removeAttribute('aria-hidden');
    document.body.classList.add('nis-locked');
    var first = q('[data-nis-close]', this.drawer);
    if (first) first.focus();
  };

  Collection.prototype.closeDrawer = function () {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('nis-locked');
    if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
  };

  /* ------------------------------------------------------ desktop dropdowns */

  Collection.prototype.toggleDrop = function (btn) {
    var panel = q('#' + btn.getAttribute('aria-controls'));
    var isOpen = btn.getAttribute('aria-expanded') === 'true';
    this.closeDrops();
    if (!isOpen && panel) {
      btn.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }
  };

  Collection.prototype.closeDrops = function () {
    qa('[data-nis-drop-btn]', this.root).forEach(function (btn) {
      btn.setAttribute('aria-expanded', 'false');
      var panel = q('#' + btn.getAttribute('aria-controls'));
      if (panel) panel.hidden = true;
    });
  };

  /* ---------------------------------------------------------------- density */

  Collection.prototype.restoreDensity = function () {
    var saved = readStore(DENSITY_KEY);
    if (saved) this.setDensity(saved, false);
    else this.applyDensityClasses();
  };

  /*
    Density is stored as "<mobile>-<desktop>". The buttons address one axis at
    a time ("m:1", "d:3") because the two breakpoints have different sensible
    counts and a shopper setting three columns on a laptop must not silently
    change what their phone shows.
  */
  Collection.prototype.setDensity = function (spec, persist) {
    var cur = (this.root.getAttribute('data-nis-cols') || '2-4').split('-');
    if (spec.indexOf(':') !== -1) {
      var parts = spec.split(':');
      if (parts[0] === 'm') cur[0] = parts[1];
      else cur[1] = parts[1];
    } else {
      cur = spec.split('-');
    }
    var value = cur[0] + '-' + (cur[1] || cur[0]);
    this.root.setAttribute('data-nis-cols', value);
    if (persist) writeStore(DENSITY_KEY, value);

    qa('[data-nis-density]', this.root).forEach(function (btn) {
      var own = btn.getAttribute('data-nis-density');
      var on;
      if (own.indexOf(':') !== -1) {
        var p = own.split(':');
        on = p[0] === 'm' ? p[1] === cur[0] : p[1] === cur[1];
      } else {
        on = own === value;
      }
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    this.applyDensityClasses();
  };

  /*
    Dawn sizes its grid with `grid--N-col-desktop` / `grid--N-col-tablet-down`
    classes, so the density toggle rewrites those rather than inventing a
    second sizing system that would then have to beat Dawn's in the cascade.
  */
  Collection.prototype.applyDensityClasses = function () {
    var value = this.root.getAttribute('data-nis-cols');
    if (!value) return;
    var parts = value.split('-');
    var mobile = parts[0];
    var desktop = parts[1] || parts[0];
    qa('ul#product-grid', this.root).forEach(function (grid) {
      grid.className = grid.className
        .split(' ')
        .filter(function (c) {
          return c.indexOf('grid--') !== 0 || c.indexOf('col-desktop') === -1;
        })
        .filter(function (c) {
          return c.indexOf('grid--') !== 0 || c.indexOf('col-tablet-down') === -1;
        })
        .join(' ');
      grid.classList.add('grid--' + desktop + '-col-desktop');
      grid.classList.add('grid--' + mobile + '-col-tablet-down');
    });
  };

  /* ------------------------------------------------------------ back to top */

  Collection.prototype.mountBackToTop = function () {
    var btn = q('[data-nis-top]', this.root);
    if (!btn) return;
    var onScroll = function () {
      btn.classList.toggle('is-on', window.scrollY > 1200);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /* -------------------------------------------------------- recently viewed */

  /*
    Recorded from the card the shopper clicks and rendered straight back out of
    localStorage. No request is made for this row at any point: everything the
    card needs is read off the card that was clicked, and its image is already
    in the browser cache from the grid it came from.

    The rail emits the SAME `.nc-*` markup the grid does, so it is literally the
    product card rather than a lookalike - including the compare-at price and
    the discount pill. `component-nische-card.css` is already on the page.
  */
  /*
    Recording moved OUT of this file. It used to listen for clicks on cards
    inside this page, which only ever caught products reached from a collection
    - not the header menu, search, the homepage, a shared link, or one product
    page linking to the next - so the rail stalled at a handful of entries no
    matter how much the shopper actually browsed.

    `snippets/nische-recently-viewed.liquid`, rendered by the product page,
    writes the same `nische:recent` key from the product's real Liquid data.
    This file only reads it.
  */

  Collection.prototype.mountRecent = function () {
    var section = q('[data-nis-recent]', this.root);
    if (!section) return;
    var rail = q('[data-nis-recent-rail]', section);

    var count = parseInt(this.root.getAttribute('data-nis-recent-count'), 10);
    if (!count || count < 1) count = 8;
    var mode = this.root.getAttribute('data-nis-recent-ratio') || 'square';

    var list = [];
    try {
      list = JSON.parse(readStore(RECENT_KEY) || '[]');
    } catch (e) {
      list = [];
    }

    var here = window.location.pathname;
    list = list
      .filter(function (row) {
        return row && row.url && row.url !== here;
      })
      .slice(0, count);

    if (!list.length || !rail) {
      section.hidden = true;
      return;
    }

    rail.innerHTML = list.map(function (row) { return recentCard(row, mode); }).join('');
    section.hidden = false;

    // Older records predate the stored ratio. Rather than leave those cards a
    // different shape, correct them once the image reports its own size.
    if (mode === 'adapt') {
      qa('img[data-nis-fixratio]', rail).forEach(function (img) {
        var apply = function () {
          if (!img.naturalWidth || !img.naturalHeight) return;
          var card = img.closest('.nc-card');
          if (card) card.style.setProperty('--nc-ratio', img.naturalWidth / img.naturalHeight);
        };
        if (img.complete) apply();
        else img.addEventListener('load', apply, { once: true });
      });
    }

    var clear = q('[data-nis-recent-clear]', section);
    if (clear) {
      clear.addEventListener('click', function () {
        writeStore(RECENT_KEY, '[]');
        section.hidden = true;
      });
    }

    this.bindRail(section, rail);
  };

  /*
    The desktop arrows. A touch device gets its affordance from the card
    peeking in at the edge of the screen; a mouse gets nothing, so these say
    "there is more this way" - and they disappear entirely when there is not.
  */
  Collection.prototype.bindRail = function (section, rail) {
    var navs = qa('[data-nis-rail]', section);
    if (!navs.length) return;

    var sync = function () {
      var overflows = rail.scrollWidth > rail.clientWidth + 2;
      navs.forEach(function (btn) {
        btn.hidden = !overflows;
      });
      if (!overflows) return;
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 2;
      navs.forEach(function (btn) {
        btn.disabled = btn.getAttribute('data-nis-rail') === 'prev' ? atStart : atEnd;
      });
    };

    navs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        // Just under a full viewport of the rail, so the card at the edge
        // stays on screen as an anchor rather than jumping past it.
        var step = Math.max(rail.clientWidth * 0.8, 200);
        rail.scrollBy({
          left: btn.getAttribute('data-nis-rail') === 'next' ? step : -step,
          behavior: 'smooth'
        });
      });
    });

    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', debounce(sync, 200));

    // Cards are fixed-width, so the rail's scrollWidth is already correct
    // before the lazy images decode - but re-check once they have, in case a
    // font swap changed a title's height and with it the layout.
    sync();
    window.setTimeout(sync, 400);
  };

  function recentCard(row, mode) {
    var ratio = 1;
    var needsFix = false;
    if (mode === 'portrait') {
      ratio = 0.8;
    } else if (mode === 'adapt') {
      if (row.ratio) ratio = row.ratio;
      else needsFix = true;
    }

    var media = '';
    if (row.img) {
      media =
        '<img src="' +
        escapeAttr(row.img) +
        '"' +
        (row.srcset ? ' srcset="' + escapeAttr(row.srcset) + '"' : '') +
        // Its own `sizes`, not the grid's: these cards are ~150px wide, and
        // inheriting a "25vw" hint would pull a desktop-sized file for them.
        ' sizes="150px" alt="' +
        escapeAttr(row.alt || '') +
        '" loading="lazy"' +
        (needsFix ? ' data-nis-fixratio' : '') +
        '>';
    }

    var prices = '<span class="nc-price">' + escapeHtml(row.price || '') + '</span>';
    if (row.was) prices += '<s class="nc-price--was">' + escapeHtml(row.was) + '</s>';
    if (row.off) prices += '<span class="nc-off">' + escapeHtml(row.off) + '</span>';

    return (
      '<a class="nc-wrap nis-recent__card" href="' +
      escapeAttr(row.url) +
      '">' +
      '<div class="nc-card" style="--nc-ratio: ' +
      ratio +
      ';">' +
      '<div class="nc-card__well"><div class="nc-card__media">' +
      media +
      '</div></div>' +
      '<div class="nc-card__body">' +
      '<div class="nc-card__titlerow"><h3 class="nc-card__title">' +
      escapeHtml(row.title) +
      '</h3></div>' +
      '<div class="nc-card__pricerow">' +
      prices +
      '</div></div></div></a>'
    );
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/'/g, '&#39;');
  }

  /* ------------------------------------------------------------------- boot */

  function boot() {
    qa('[data-nis-col]').forEach(function (root) {
      if (root.nisBound) return;
      root.nisBound = true;
      var page = new Collection(root);
      page.paintPriceFill();
      root.nisPage = page;
    });
  }

  window.NischeCollection = { boot: boot };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
